import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const CompanySettingsContext = createContext();

export const useCompanySettings = () => {
  const context = useContext(CompanySettingsContext);
  if (!context) {
    throw new Error('useCompanySettings must be used within a CompanySettingsProvider');
  }
  return context;
};

export const CompanySettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    companyName: 'Şirket Adı',
    logo: null,
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    timezone: 'Europe/Istanbul',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: 'HH:mm',
    currency: 'TRY',
    language: 'tr',
    // Default access controls
    accessControls: {
      canViewReports: true,
      canManageEmployees: true,
      canManageDepartments: true,
      canManageShifts: true,
      canManageLeave: true,
      canManageAttendance: true,
      canManagePayroll: true,
      canManageSettings: true,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load company settings when component mounts
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // In a real app, you would fetch these settings from your API
        // const response = await api.get('/api/company/settings');
        // setSettings(response.data);
        
        // For now, we'll use the default settings
        setLoading(false);
      } catch (err) {
        console.error('Failed to load company settings:', err);
        setError('Şirket ayarları yüklenirken bir hata oluştu');
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Admin users have all permissions
    if (user.role === 'admin') return true;
    
    // Check specific permission
    return settings.accessControls[permission] === true;
  };

  // Alias for hasPermission for better readability
  const can = (permission) => hasPermission(permission);

  // Common permissions
  const canViewReports = hasPermission('canViewReports');
  const canManageEmployees = hasPermission('canManageEmployees');
  const canManageDepartments = hasPermission('canManageDepartments');
  const canManageShifts = hasPermission('canManageShifts');
  const canManageLeave = hasPermission('canManageLeave');
  const canManageAttendance = hasPermission('canManageAttendance');
  const canManagePayroll = hasPermission('canManagePayroll');
  const canManageSettings = hasPermission('canManageSettings');

  const value = {
    settings,
    loading,
    error,
    hasPermission,
    can,
    canViewReports,
    canManageEmployees,
    canManageDepartments,
    canManageShifts,
    canManageLeave,
    canManageAttendance,
    canManagePayroll,
    canManageSettings,
  };

  return (
    <CompanySettingsContext.Provider value={value}>
      {!loading && children}
    </CompanySettingsContext.Provider>
  );
};

export default CompanySettingsContext;
